import { Component, Input, OnInit } from '@angular/core';
import { LoadingController, ModalController, Platform, ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import _ from 'lodash';

import { ConfigService } from 'src/app/provider/config.service';
import { ApiService } from 'src/app/provider/api.service';
import { endpoints } from 'src/app/constant/endpoints';
import { AuthService } from 'src/app/services/auth.service';
import { Events } from 'src/app/provider/events.service';

@Component({
    selector: 'app-limited-modal',
    templateUrl: './limited-modal.page.html',
    styleUrls: ['./limited-modal.page.scss'],
})
export class LimitedModalPage implements OnInit {
    @Input() title: any;
    @Input() reason: any;
    @Input() selected: any;
    @Input() unselected: any;

    settings: any = {};
    customer: any;
    maximumAllergySelection: number = 0;
    inventoriesArr: any[] = [];
    orginalList: any[] = [];
    selectedArr: any[] = [];
    loader: boolean = true;

    constructor(
        private events: Events,
        private platform: Platform,
        private modalCtrl: ModalController,
        private loadingCtrl: LoadingController,
        private toastCtrl: ToastController,
        private apiService: ApiService,
        private configService: ConfigService,
        private authService: AuthService,
        private translate: TranslateService,
    ) {}
    async ngOnInit() {
        try {
          this.customer = await this.authService.getAuthUser();
          const appPayload = await this.configService.getStorage('appPayload');
          this.maximumAllergySelection = Number(
            appPayload?.configurations?.settings?.maximum_allergy_selection || 0
          );
          const settingData = await this.configService.getAppSettings();
          this.settings = JSON.parse(typeof settingData === 'string' ? settingData : '{}');
          this.translate.setDefaultLang(this.settings.appLanguage);
          this.translate.use(this.settings.appLanguage);
        } catch (error) {
          console.error('Error in ngOnInit:', error);
        }
      }
    
      async ionViewWillEnter() {
        try {
            this.customer = await this.authService.getAuthUser();
            if (this.customer) {
                // Refresh inventories on modal reopen
                this.allInventories();
            }
        } catch (error) {
            console.error('Error in ionViewWillEnter:', error);
        }
    }
    
      // Fetch inventories and handle selection logic
      allInventories = () => {
        this.loader = true;
        this.apiService
          .get(`${endpoints[this.reason]}?customer_id=${this.customer.id}`)
          .subscribe((response) => {
            this.loader = false;
            if (response.success) {
              let inventories = response.data[this.reason];
    
              // Mark selected items as checked
              if (this.selected && this.selected.length) {
                const selectedSlugs = new Set(this.selected.map(item => item.slug));
                inventories.forEach(item => {
                  item.checked = selectedSlugs.has(item.slug);  // Mark selected items as checked
                });
              }
    
              // Mark unselected items as disabled
              if (this.unselected && this.unselected.length) {
                const unselectedSlugs = new Set(this.unselected.map(item => item.slug));
                inventories.forEach(item => {
                  item.disabled = unselectedSlugs.has(item.slug);  // Disable unselected items
                });
              }
    
              this.inventoriesArr = [...inventories];
              this.orginalList = [...inventories];
            } else if (response.code === 401) {
              this.events.publish('user:login', { user: null, time: new Date() });
            }
          });
      };
    
      // Handle item selection/deselection
      selectInventory = (event, item) => {
        item.checked = event.target.checked;
    
        // Check if the item is already selected as allergy or dislike
        const isSelectedAsAllergy = this.selected.some(sel => sel.slug === item.slug);
        const isSelectedAsDislike = this.unselected.some(unsel => unsel.slug === item.slug);
    
        if (this.reason === 'allergies' && isSelectedAsDislike) {
            // If the item is already a dislike, prevent selecting as allergy
            item.checked = false;
            item.disabled = true;
            this.toastMessage('This item cannot be selected as both allergy and dislike.');
            return;
        } else if (this.reason === 'dislikes' && isSelectedAsAllergy) {
            // If the item is already an allergy, prevent selecting as dislike
            item.checked = false;
            item.disabled = true;
            this.toastMessage('This item cannot be selected as both allergy and dislike.');
            return;
      
        }
    
        const selectedAllergiesCount = this.inventoriesArr.filter(i => i.checked && i.type === 'allergy').length;
        const selectedDislikesCount = this.inventoriesArr.filter(i => i.checked && i.type === 'dislike').length;
    
        // Separate max selection limits for allergies and dislikes
        const disableAllergies = selectedAllergiesCount >= this.maximumAllergySelection;
        const disableDislikes = selectedDislikesCount >= this.maximumAllergySelection;
    
        this.inventoriesArr.forEach(i => {
            // If the item is not selected, apply the appropriate disable logic
            if (!i.checked) {
                if (i.type === 'allergy') {
                    // Disable allergy items if the limit is reached or if it's already selected as a dislike
                    if (disableAllergies || this.selected.some(selItem => selItem.slug === i.slug && selItem.type === 'dislike')) {
                        i.disabled = true;
                    } else {
                        i.disabled = false;
                    }
                } else if (i.type === 'dislike') {
                    // Disable dislike items if the limit is reached or if it's already selected as an allergy
                    if (disableDislikes || this.selected.some(selItem => selItem.slug === i.slug && selItem.type === 'allergy')) {
                        i.disabled = true;
                    } else {
                        i.disabled = false;
                    }
                }
            }
        });

    };
      // Get selected inventory items count
      getSelectedInventory = () => {
        return this.inventoriesArr.filter(item => item.checked).length;
      };
    
      // Search through the inventory list
      getList = (ev: any) => {
        const val = ev.target.value.trim().toLowerCase();
        if (val) {
          this.inventoriesArr = this.orginalList.filter(inventory =>
            this.platform.isRTL
              ? inventory.title.includes(val) || inventory.locale_title.toLowerCase().includes(val)
              : inventory.title.toLowerCase().includes(val)
          );
        } else {
          this.clearLists();
        }
      };
    
      // Clear search results and show all items
      clearLists = () => {
        this.inventoriesArr = [...this.orginalList];
      };
    
      // Confirm selection and close the modal
      submitData = async () => {
        const selectedArr = this.inventoriesArr
          .filter(inventory => inventory.checked)
          .map(i => i.id);
    
        let updateQuery: { customer_id: number; [key: string]: any } = {
          customer_id: this.customer.id
        };
    
        if (this.reason === 'allergies') {
          updateQuery.allergy_ids = selectedArr;
        } else {
          updateQuery.dislike_ids = selectedArr;
        }
    
        this.apiService.post(endpoints[this.reason], updateQuery).subscribe((response) => {
          if (response.success) {
            this.modalCtrl.dismiss(response.message, 'close');
          } else if (response.code === 401) {
            this.events.publish('user:login', { user: null, time: new Date() });
          }
          this.toastMessage(response.message);
        });
      };

    toastMessage = async (msg) => {
        await this.toastCtrl.create({
            message: this.translate.instant(msg),
            duration: 4000,
            position: 'top',
            color: 'light'
        }).then((toastElem) => toastElem.present());
    }
    showLoading = async () => {
        this.loadingCtrl.create({
            message: this.translate.instant('please_wait'),
            cssClass: 'loader_class',
        }).then(a => {
            a.present().then(() => {
                console.log('presented');

            });
        });
    }
    hideLoading = async () => {
        setTimeout(async () => {
            try {
                this.loadingCtrl.dismiss();
            } catch (error) {
                console.log(error);
            }
        }, 1000);
    }
    closeModal = () => {
        this.modalCtrl.dismiss(null, 'cancel');
    }
}